import { useState, useEffect } from 'react';
import { Snackbar, Alert } from '@mui/material';
import axios from 'axios';

// Relative Imports
import './CSS/App.css';
import C from './Constants/Constants';
import TopBar from './Components/TopBar/TopBar';
import LandingPage from './Components/LandingPage/LandingPage';
import LoadingPage from './Components/LoadingPage/LoadingPage';
import ErrorPage from './Components/ErrorPage/ErrorPage';


function App() {
  // Use a props object to pass props to child components
  const props = {};

  // Only pass states to props and not the setting functions

  // User State
  const [userState, setUserState] = useState(C.USER_STATE.LOADING);
  props.userState = userState;
  props.setUserState = setUserState;

  // Email and Password
  const [currentEmail, setCurrentEmail] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');

  // Cognito Attributes
  const [cognitoAttributes, setCognitoAttributes] = useState({});
  props.cognitoAttributes = cognitoAttributes;
  
  // Configuration from Server
  const [config, setConfig] = useState({});
  props.config = config;
  
  // Global props that states if the app is communicating with the server
  // Useful for disabling buttons while communicating
  const [isCommunicating, setIsCommunicating] = useState(false);
  props.isCommunicating = isCommunicating;
  props.setIsCommunicating = setIsCommunicating;

  // Snackbar states
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  props.snackbarMessage = snackbarMessage;
  props.snackbarSeverity = snackbarSeverity;
  props.snackbarOpen = snackbarOpen;
  props.snackbarClose = () => {
    setSnackbarOpen(false);
  };

  // Whether urlparams has a signup=true parameter
  const [isSignup, setIsSignup] = useState(false);
  props.isSignup = isSignup; 

  // The following states will not be passed to child components
  // DEV: idToken not yet used as state, will be used later
  const [idToken, setIdToken] = useState(undefined);


  // On App Mount
  useEffect(() => {
    // Check if website is being loaded from an iframe
    if (window.self !== window.top) {
      console.log("Loaded from iframe.");
      // Delete all localStorage items
      localStorage.clear();

    } else {
      console.log("Loaded normally.");
    }

    async function loadConfigAndAuthenticate() {
      setIsCommunicating(true);
      // Fetch Config Data from C.API_CONFIG
      let configData;
      try {
        configData = await axios.get(C.API_CONFIG, {
          headers: {
            'Content-Type': 'application/json',
          },
        });

      } catch (error) {
        console.error('Config request failed:', error);
        setUserState(C.USER_STATE.ERROR);
          setIsCommunicating(false);
          return;
      }

      if (configData?.data?.code !== 'SUCCESS') {
        console.log('ERROR: server error on config request.');
        setUserState(C.USER_STATE.ERROR);
          setIsCommunicating(false);
          return;
      }
      
      setConfig(configData.data?.data);

      // Check if the URL has a resetpassword parameter and a value
      const urlParams = new URLSearchParams(window.location.search);
      const resetPassword = urlParams.get('reset_token');
      if (resetPassword !== null) {
        setUserState(C.USER_STATE.RESET_PASSWORD);
        setIsCommunicating(false);
        return;
      }
      
      authenticate();
    }

    loadConfigAndAuthenticate();

  },[]);


  async function authenticate() {

    setIsCommunicating(true);
    setUserState(C.USER_STATE.LOADING);

    // // Capture the 'code' parameter from the query string
    // const urlParams = new URLSearchParams(window.location.search);
    // const code = urlParams.get('code');

    // If code is undefined, check if the idToken is in the cookie
    let idToken;
    let accessToken;
    let refreshToken;
    let identityId;
    // if (code === null) {
      
    // Get idToken from localStorage, not cookies
    const lastUser = localStorage.getItem(`CognitoIdentityServiceProvider.${C.CLIENT_ID}.LastAuthUser`);
    if (lastUser === null || lastUser === undefined) {
      console.log('LOGGED_OUT: No idToken found in localStorage.');
      // Check if the URL has a signup=true parameter
      const urlParams = new URLSearchParams(window.location.search);
      const signup = urlParams.get('signup') === 'true';
      setUserState(signup ? C.USER_STATE.SIGN_UP : C.USER_STATE.LOGGED_OUT);
      setIsCommunicating(false);
      setIsSignup(signup);
      return;
    }

    idToken = localStorage.getItem(`CognitoIdentityServiceProvider.${C.CLIENT_ID}.${lastUser}.idToken`);
    accessToken = localStorage.getItem(`CognitoIdentityServiceProvider.${C.CLIENT_ID}.${lastUser}.accessToken`);
    refreshToken = localStorage.getItem(`CognitoIdentityServiceProvider.${C.CLIENT_ID}.${lastUser}.refreshToken`);
    identityId = localStorage.getItem(`aws.congnito.identity-id.${C.REGION}.${C.IDENTITY_POOL_ID}`);

    if (idToken === null || idToken === undefined) {
      console.log('LOGGED_OUT: No idToken found in cookie.');
      setUserState(C.USER_STATE.LOGGED_OUT);
      setIsCommunicating(false);
      return;
    }
    // } else {

    //   // Make a POST request to the token endpoint
    //   const tokenEndpoint = 'https://2mw.auth.us-east-2.amazoncognito.com/oauth2/token';
    //   const clientId = '33i2thuc63c7nq2bqqfi07rg7s';
    //   const redirectUri = `${C.BASE_URL}/Profile.html`;

    //   let authResult;
    //   try {
    //     authResult = await axios.post(tokenEndpoint, {
    //       'grant_type': 'authorization_code',
    //       'code': code,
    //       'client_id': clientId,
    //       'redirect_uri': redirectUri,
    //       'scope': 'openid aws.cognito.signin.user.admin',
    //     }, {
    //       headers: {
    //         'Content-Type': 'application/x-www-form-urlencoded',
    //       },
    //     });
    //     console.log('authResult', authResult?.data?.id_token);
    //     // Decode the ID token to retrieve Cognito attributes
    //     idToken = authResult?.data?.id_token;
    //     refreshToken = authResult?.data?.refresh_token;
    //     accessToken = authResult?.data?.access_token;
    //   } catch (error) {
    //     console.error('Token request failed:', error);
    //     setUserState(C.USER_STATE.ERROR);
    //     setIsCommunicating(false);
    //     return;
    //   }

    //   AWS.config.region = C.REGION;
    //   AWS.config.credentials = new AWS.CognitoIdentityCredentials({
    //     IdentityPoolId: C.IDENTITY_POOL_ID,
    //     Logins: {
    //       [`cognito-idp.us-east-2.amazonaws.com/${C.USER_POOL_ID}`]: authResult?.data?.id_token
    //     }
    //   });

    //   // Create a new CognitoIdentity object
    //   const cognitoIdentity = new AWS.CognitoIdentity();

    //   // Call the GetId API to retrieve the identity ID
      
    //   try {
    //     const cognitoResult = await cognitoIdentity.getId({
    //       IdentityPoolId: C.IDENTITY_POOL_ID,
    //       Logins: {
    //         [`cognito-idp.us-east-2.amazonaws.com/${C.USER_POOL_ID}`]: authResult?.data?.id_token
    //       }
    //     }).promise();

    //     console.log('cognitoResult', cognitoResult);
    //     identityId = cognitoResult?.IdentityId;

    //   } catch (error) {
    //     console.error('Token request failed:', error);
    //     setUserState(C.USER_STATE.ERROR);
    //     setIsCommunicating(false);
    //     return;
    //   }
    // }

    // If idToken is undefined, set userState to logged out
    if (idToken === undefined) {
      console.log('LOGGED_OUT: Auth returned undefined idToken.');
      setUserState(C.USER_STATE.LOGGED_OUT);
      setIsCommunicating(false);
      return;
    }

    // Verify the ID token by making a GET request to C.API_IDTOKEN_VERIFICATION
    // If the ID token is invalid, set userState to logged out
    let verificationResult;
    try {
      console.log(C.API_IDTOKEN_VERIFICATION);
      verificationResult = await axios.get(C.API_IDTOKEN_VERIFICATION, {
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': C.API_KEY,
          'x-user-token': 'Bearer ' + idToken,
        },
      });
      // verificationResult = await verificationResult.json();
      console.log({verificationResult});
      if (verificationResult?.data?.code !== 'SUCCESS') {
        console.log('LOGGED_OUT: verificationResult does not contain idToken.');
        setUserState(C.USER_STATE.LOGGED_OUT);
        setIsCommunicating(false);
        return
      }
    } catch (error) {
      console.log({error});
      if (error?.response?.status === 401) {
        console.log('LOGGED_OUT: 401 Unauthorized idTokenVerification API.');
        setUserState(C.USER_STATE.LOGGED_OUT);
        setIsCommunicating(false);
        return;
      }
      console.log('LOGGED_OUT: Unknown error.');
      setUserState(C.USER_STATE.ERROR);
      setIsCommunicating(false);
      return;
    }

    // If any error occurred while decoding the token
    // set userState to ERROR
    let cognitoAttributes;
    console.log({idToken});
    try {
      cognitoAttributes = decodeJWT(idToken);
      console.log({cognitoAttributes});
      if (cognitoAttributes === undefined) {
        console.log('ERROR: Error decoding idToken.');
        setUserState(C.USER_STATE.ERROR);
        setIsCommunicating(false);
        return;
      }
    } catch {
      setUserState(C.USER_STATE.ERROR);
        setIsCommunicating(false);
        return;
    }

    // Remove all string after the '?' in the URL
    const newUrl = window.location.href.split('?')[0];
    window.history.pushState({}, '', newUrl);

    // Set the cognitoAttributes state
    const newUserState = getUserState(cognitoAttributes);

    // Write to localstorage
    localStorage.setItem(`CognitoIdentityServiceProvider.${C.CLIENT_ID}.${cognitoAttributes['cognito:username']}.idToken`,
      idToken);
    localStorage.setItem(`CognitoIdentityServiceProvider.${C.CLIENT_ID}.${cognitoAttributes['cognito:username']}.accessToken`,
      accessToken);
    localStorage.setItem(`CognitoIdentityServiceProvider.${C.CLIENT_ID}.${cognitoAttributes['cognito:username']}.refreshToken`,
      refreshToken);
    localStorage.setItem(`CognitoIdentityServiceProvider.${C.CLIENT_ID}.LastAuthUser`,
      cognitoAttributes['cognito:username']);
    localStorage.setItem(`aws.congnito.identity-id.${C.REGION}.${C.IDENTITY_POOL_ID}`,
      identityId);
    localStorage.setItem(`aws.congnito.identity-providers.${C.REGION}.${C.IDENTITY_POOL_ID}`,
      `cognito-idp.us-east-2.amazonaws.com/${C.USER_POOL_ID}`);
    
    if (newUserState === C.USER_STATE.COMPLETE) {
      // Check if in iframe
      if (window.self !== window.top) {
        // Send message to parent window
        console.log('Sending message to parent window')
        window.parent.postMessage({
          type: '2mwCredentials',
          data: {
            idToken: {
              payload: cognitoAttributes,
              jwtToken: idToken,
            },
            refreshToken: {
              token: refreshToken,
            },
          },
        }, '*');
        return;
      } else {
        // Redirect to home page
        console.log('Redirecting to home page')
        window.location.href = C.URL_LANDING;
        return;
      }
    }
    
    setIdToken(idToken);
    setCognitoAttributes(cognitoAttributes);
    setUserState(newUserState);
    setIsCommunicating(false);
    
  };
  
  props.handlerOnSubmitResetPassword = async (data) => {
    setIsCommunicating(true);

    // Make a POST request to reset password endpoint
    try {
      await axios
        .post(C.API_RESET_PASSWORD, data, {
          headers: {
            'Content-Type': 'application/json',
            'x-api-key': C.API_KEY,
          },
        })
    } catch(error) {
        console.log({error});
        // Returned server messages are in error.response.data
        const errRsp = error?.response?.data;
        let message = errRsp === undefined ? error.message : errRsp.message;

        // Default Axios network error. Make it more user friendly.
        if (message === 'Network Error') message = 'Network error. Please try again again.';

        setSnackbarMessage(message);
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
        setIsCommunicating(false);
        return;
    }
    
    // If no error occurred, redirect to reset password confirmation page
    setUserState(C.USER_STATE.RESET_PASSWORD_SUCCESS);
    setIsCommunicating(false);

  }

  props.handlerOnSubmitSignUp = async (data) => {
    console.log({data});
    setIsCommunicating(true);

    // Make a POST request to sign up endpoint
    try {
      await axios
        .post(C.API_SIGN_UP, data, {
          headers: {
            'Content-Type': 'application/json',
          },
        })
    } catch(error) {
        console.log({error});
        // Returned server messages are in error.response.data
        const errRsp = error?.response?.data;
        let message = errRsp === undefined ? error.message : errRsp.message;

        // Default Axios network error. Make it more user friendly.
        if (message === 'Network Error') message = 'Network error. Please try again again.';

        setSnackbarMessage(message);
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
        setIsCommunicating(false);
        return;
    };
  
    // If no error occurred, log user in
    setSnackbarMessage('Successfully created account. Logging you in...');
    setSnackbarSeverity('success');
    setSnackbarOpen(true);

    setUserState(C.USER_STATE.LOGGED_OUT);
    props.handlerOnSubmitLogin(data.email, data.password)

  };

  props.handlerOnSubmitLogin = async (email, password) => {
    setIsCommunicating(true);

    // Make a POST request to login endpoint
    try {
      const response = await axios
        .post(C.API_LOGIN, {
          email,
          password,
        }, {
          headers: {
            'Content-Type': 'application/json',
            'x-api-key': C.API_KEY,
          },
        });
      console.log({response});
      const { idToken, accessToken, refreshToken, identityId } = response.data.data;
      console.log({idToken, accessToken, refreshToken, identityId });

      setIdToken(idToken);
      
      // Write to localstorage
      localStorage.setItem(`CognitoIdentityServiceProvider.${C.CLIENT_ID}.${cognitoAttributes['cognito:username']}.idToken`,
        idToken);
      localStorage.setItem(`CognitoIdentityServiceProvider.${C.CLIENT_ID}.${cognitoAttributes['cognito:username']}.accessToken`,
        accessToken);
      localStorage.setItem(`CognitoIdentityServiceProvider.${C.CLIENT_ID}.${cognitoAttributes['cognito:username']}.refreshToken`,
        refreshToken);
      localStorage.setItem(`CognitoIdentityServiceProvider.${C.CLIENT_ID}.LastAuthUser`,
        cognitoAttributes['cognito:username']);
      localStorage.setItem(`aws.congnito.identity-id.${C.REGION}.${C.IDENTITY_POOL_ID}`,
        identityId);
      localStorage.setItem(`aws.congnito.identity-providers.${C.REGION}.${C.IDENTITY_POOL_ID}`,
        `cognito-idp.us-east-2.amazonaws.com/${C.USER_POOL_ID}`);
    } catch(error) {
      console.log({error});
      // Returned server messages are in error.response.data
      const errRsp = error?.response?.data;
      let message = errRsp === undefined ? error.message : errRsp.message;

      // Default Axios network error. Make it more user friendly.
      if (message === 'Network Error') message = 'Network error. Please try again again.';

      setSnackbarMessage(message);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      setIsCommunicating(false);
      return;
    }

    setSnackbarMessage('Successfully logged in!');
    setSnackbarSeverity('success');
    setSnackbarOpen(true);
    setIsCommunicating(false);
    setCurrentEmail(email);
    setCurrentPassword(password);

    authenticate();    
  }


  props.handlerOnSubmitProfileUpdate = async (data) => {
    console.log({data});
    setIsCommunicating(true);

    // Make a POST request to profile update endpoint
    try {
      await axios.post(C.API_PROFILE_UPDATE, data, {
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': C.API_KEY,
          'x-user-token': 'Bearer ' + idToken,
        },
      });
    } catch (error) {

      console.log({error});
      // Returned server messages are in error.response.data

      const errRsp = error?.response?.data;
      let message = errRsp === undefined ? error.message : errRsp.message;

      // Default Axios network error. Make it more user friendly.
      if (message === 'Network Error') message = 'Network error. Please try again again.';

      setSnackbarMessage(message);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      setIsCommunicating(false);
      return;
    }
      

    // Two situations: if email and password are available, log user in again
    // to refresh tokens and corresponding attributes
    // Otherwise, log user out and have them log in again
    if (currentEmail !== '' && currentPassword !== '') {
      setSnackbarMessage('Profile updated successfully. Logging you back in...');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      setIsCommunicating(true);
      setUserState(C.USER_STATE.LOGGED_OUT);
      props.handlerOnSubmitLogin(currentEmail, currentPassword);
    } else {
      setSnackbarMessage('Profile updated successfully. Please log in again.');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      setIsCommunicating(false);
      setUserState(C.USER_STATE.LOGGED_OUT);
    }
  };

  return (
    <div id="pageLanding">
      <div className='container'>
        { props.userState === C.USER_STATE.LOADING ? <LoadingPage /> : null }
        { props.userState === C.USER_STATE.ERROR ? <ErrorPage /> : null}
        { [
            C.USER_STATE.COMPLETE,
            C.USER_STATE.LOADING,
            C.USER_STATE.ERROR
          ].indexOf(props.userState) < 0 ?
          <>
            <TopBar />
            <LandingPage {...props}/>
          </>
          : null}
      </div>
      <Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={props.snackbarClose}>
        <Alert onClose={props.snackbarClose} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </div>
  );
}

/* =========== Helper Functions =========== */

// Helper Function to decode the JWT token
function decodeJWT(token) {
  const base64Url = token.split('.')[1];
  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
  const jsonPayload = decodeURIComponent(atob(base64).split('').map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)).join(''));
  const parsedJson = JSON.parse(jsonPayload);
  return [undefined, null].indexOf(parsedJson) > -1 ? undefined : parsedJson;
}

// Helper Function to determine the user state
function getUserState(cognitoAttributes) {
  console.log({cognitoAttributes});
  if (cognitoAttributes?.email_verified === undefined) {
    // At this point, cognitoAttributes or email_verified is expected to be defined.
    // If it is not, then there is an error.
    console.log('ERROR: getUserState, email_verified is undefined.');
    return C.USER_STATE.ERROR;
  }

  console.log({cognitoAttributes});
  
  if (cognitoAttributes.email_verified === false) {
    console.log('getUserState: email not verified.')
    return C.USER_STATE.UNCONFIRMED;
  }

  if ([undefined, ''].indexOf(cognitoAttributes.email) > -1) {
    console.log('getUserState: email not defined.')
    return C.USER_STATE.INCOMPLETE;
  }

  if ([undefined, ''].indexOf(cognitoAttributes['cognito:username']) > -1) {
    console.log('getUserState: username not defined.')
    return C.USER_STATE.INCOMPLETE;
  }

  if ([undefined, ''].indexOf(cognitoAttributes.given_name) > -1) {
    console.log('getUserState: firstName not defined.')
    return C.USER_STATE.INCOMPLETE;
  }

  if ([undefined, ''].indexOf(cognitoAttributes.family_name) > -1) {
    console.log('getUserState: lastname not defined.')
    return C.USER_STATE.INCOMPLETE;
  }

  if ([undefined, ''].indexOf(cognitoAttributes["custom:teamName"]) > -1) {
    console.log('getUserState: teamName not defined.')
    return C.USER_STATE.INCOMPLETE;
  }

  if ([undefined, ''].indexOf(cognitoAttributes["custom:teamAbbrev"]) > -1) {
    console.log('getUserState: teamAbbrev not defined.')
    return C.USER_STATE.INCOMPLETE;
  }

  console.log('Profile is complete.');
  return C.USER_STATE.COMPLETE;

}

export default App;